import type { ChapterItem } from '@app/_proto/Protos/chapters';
import type { NovelItem } from '@app/_proto/Protos/novels';
import Loading from '@app/components/loading';
import SneekPeekLock from '@app/domains/champion/components/ChampionSneakPeekDialog';
import useChampionSubscriptions from '@app/domains/champion/hooks/useChampionSubscriptions';
import AdvanceChapterLock from '@app/domains/chapter/components/AdvancedChapterLock';
import AnonymousChapterLock from '@app/domains/chapter/components/AnonymousChapterLock';
import ChapterKarmaPaywallFetcher from '@app/domains/chapter/components/ChapterKarmaPaywallFetcher';
import ChapterLockLogger from '@app/domains/chapter/components/ChapterLockLogger';
import ChapterWaitToUnlockPaywallFetcher from '@app/domains/chapter/components/ChapterWaitToUnlockPaywallFetcher';
import { checkHavingSneakPeekAccess } from '@app/domains/chapter/utils';
import useChapterLockStatus from '@app/domains/common/hooks/useChapterLockStatus';
import useMyVouchers from '@app/domains/common/hooks/useMyVouchers';
import useNovelStatus from '@app/domains/common/hooks/useNovelStatus';
import { getNovelPath } from '@app/domains/common/utils/path';
import { useAuth } from '@app/libs/auth';
import { useNavigate } from 'react-router-dom';

export type ChapterLockProps = {
    novel: NovelItem;
    chapter: ChapterItem;
    onReloadChapter: () => void;
    onUnlockChapter: ({ on, type }: { on: string; type: string }) => void;
};

export default function ChapterLock({ novel, chapter, onReloadChapter, onUnlockChapter }: ChapterLockProps) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { isSneakPeekNovel } = useNovelStatus({ novel });
    const {
        isLockedByAdvancedChapter,
        isLockedByWaitToUnlockChapter,
        isLockedByKarmaChapter,
        isChapterLocked,
        isChapterLockedByVoucher,
    } = useChapterLockStatus({
        novel,
        chapter,
    });

    const { activeSubscription } = useChampionSubscriptions({
        suspense: true,
    });

    const hasAnyActiveChampionShip = !!activeSubscription;

    const { chaptersVoucherCounts } = useMyVouchers();

    const handleCloseSneakPeekLock = () => {
        navigate(getNovelPath(novel.slug));
    };


    if (!user && isChapterLocked) {
        return (
            <ChapterLockLogger novel={novel} chapter={chapter} type={'Anonymous Chapter Lock'}>
                <AnonymousChapterLock />
            </ChapterLockLogger>
        );
    }

    if (!!novel && !!chapter && isSneakPeekNovel && !user?.isVipActive && !hasAnyActiveChampionShip) {
        if (checkHavingSneakPeekAccess({ user: user ?? undefined, novelId: novel.id })) return null;
        return (
            <Loading displayType="indicator">
                <ChapterLockLogger novel={novel} chapter={chapter} type={'VIP Sneak Peek Lock'}>
                    <SneekPeekLock onClose={handleCloseSneakPeekLock} />
                </ChapterLockLogger>
            </Loading>
        );
    }

    if (!!novel && !!chapter && isLockedByAdvancedChapter) {
        return (
            <Loading displayType="indicator">
                <ChapterLockLogger novel={novel} chapter={chapter} type={'Advance Chapter Lock'}>
                    <AdvanceChapterLock novel={novel} onSubscribed={onReloadChapter} />
                </ChapterLockLogger>
            </Loading>
        );
    }

    if (!!novel && !!chapter && isLockedByWaitToUnlockChapter) {
        return (
            <Loading displayType="indicator">
                <ChapterLockLogger novel={novel} chapter={chapter} type={'WTU Lock'}>
                    <ChapterWaitToUnlockPaywallFetcher
                        novel={novel}
                        chapter={chapter}
                        onUnlockChapter={onUnlockChapter}
                        onReloadChapter={onReloadChapter}
                        canChapterUnlockByVoucher={isChapterLockedByVoucher}
                        availableVouchers={chaptersVoucherCounts ?? 0}
                    />
                </ChapterLockLogger>
            </Loading>
        );
    }
    if (!!novel && !!chapter && isLockedByKarmaChapter) {
        return (
            <Loading displayType="indicator">
                <ChapterLockLogger novel={novel} chapter={chapter} type={'Karma Lock'}>
                    <ChapterKarmaPaywallFetcher
                        novel={novel}
                        chapter={chapter}
                        onUnlockChapter={onUnlockChapter}
                        onReloadChapter={onReloadChapter}
                        canChapterUnlockByVoucher={isChapterLockedByVoucher}
                        availableVouchers={chaptersVoucherCounts ?? 0}
                    />
                </ChapterLockLogger>
            </Loading>
        );
    }

    return null;
}
